import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  Permissions as P,
  type NatwestApplicationWorkspace as Model,
  type NatwestApplicationPackage as Package,
} from "@myfm/domain";
import { api, authenticatedFetch } from "../api";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateOnly, formatDateTime, formatTimestampDate } from "../dates";
import { isApiError } from "../api-error";
import { useCapability } from "../ui/StaffSession";
import { RecordDraftBoundary, useRecordDraft } from "../ui/RecordDraftBoundary";
import { saveRequest } from "../ui/save-request";
import { ErrorState, Loading, reviewedApplicationError } from "../ui/State";
import { Link } from "../ui/ScopedLink";
import { OnboardingDocumentUpload } from "./OnboardingDocumentUpload";
import { NatwestPreparation } from "./NatwestPreparation";
import { NatwestBankProgress } from "./NatwestBankProgress";

export type NatwestCommand = (
  path: string,
  body: Record<string, unknown>,
) => Promise<boolean>;
export function NatwestApplication({ building }: { building: string }) {
  const finance = useCapability(P.ViewFinance),
    onboarding = useCapability(P.ViewClientOnboarding),
    documents = useCapability(P.ViewDocuments),
    restricted = useCapability(P.ViewRestrictedDocuments);
  if (!finance || !onboarding || !documents || !restricted)
    return (
      <p>
        Restricted finance, onboarding and document access is required to
        prepare or review bank applications.
      </p>
    );
  return (
    <RecordDraftBoundary subject="This NatWest application">
      <Application key={building} building={building} />
    </RecordDraftBoundary>
  );
}
function Application({ building }: { building: string }) {
  const root = `/api/building-setup/${building}/natwest-application`;
  const [model, setModel] = useState<Model>(),
    [error, setError] = useState<unknown>(),
    [notice, setNotice] = useState(""),
    [busy, setBusy] = useState(false),
    [held, setHeld] = useState(false),
    [editing, setEditing] = useState(false);
  const request = useRef<
      { path: string; body: Record<string, unknown> } | undefined
    >(undefined),
    lock = useRef(false);
  useRecordDraft(busy || held);
  async function load() {
    const value = await api<Model>(root + `?building=${building}`);
    if (value.building !== building)
      throw reviewedApplicationError(
        "The application does not match this building.",
      );
    setModel(value);
  }
  useEffect(() => {
    const reload = () => {
      if (!lock.current) void load().catch(setError);
    };
    reload();
    window.addEventListener("canonical-record-saved", reload);
    return () => window.removeEventListener("canonical-record-saved", reload);
  }, [root]);
  const command: NatwestCommand = async (path, body) => {
    if (lock.current || !model) return false;
    lock.current = true;
    setBusy(true);
    setError(undefined);
    setNotice("");
    try {
      const original = request.current ?? {
        path,
        body: {
          ...body,
          sourceHash: body.sourceHash ?? model.sourceHash,
          idempotencyKey: crypto.randomUUID(),
        },
      };
      request.current = original;
      const receipt = await saveRequest<{ building: string }>(
        root + "/" + original.path + `?building=${building}`,
        { method: "POST", body: JSON.stringify(original.body) },
      );
      if (receipt.building !== building)
        throw reviewedApplicationError(
          "The application save was not confirmed. Check the original request.",
        );
      request.current = undefined;
      setHeld(false);
      await load().catch(setError);
      window.dispatchEvent(new Event("canonical-record-saved"));
      setNotice("Application saved.");
      return true;
    } catch (cause) {
      setError(cause);
      const uncertain =
        Boolean(request.current) &&
        (!isApiError(cause) ||
          cause.statusCode === 0 ||
          cause.statusCode >= 500);
      setHeld(uncertain);
      if (!uncertain) request.current = undefined;
      if (isApiError(cause) && cause.statusCode === 409)
        await load().catch(() => {});
      return false;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  async function mail(version: Package, action: "review" | "send") {
    if (lock.current || !version.draftId || !version.email) return;
    lock.current = true;
    setBusy(true);
    setError(undefined);
    setNotice("");
    try {
      const body =
        action === "send"
          ? { expectedVersion: version.email.version, confirmation: "SEND" }
          : {
              expectedVersion: version.email.version,
              decision: "Approved",
              sourcePackageHash: version.packageHash,
              note: "Exact NatWest application email and retained attachments reviewed",
            };
      const value = await api<{ id: string; status: string }>(
        `/api/communications/drafts/${version.draftId}/${action}?building=${building}`,
        { method: "POST", body: JSON.stringify(body) },
      );
      if (
        value.id !== version.draftId ||
        value.status !== (action === "send" ? "Sent" : "Approved")
      )
        throw reviewedApplicationError(
          "Mail did not confirm the expected result.",
        );
      window.dispatchEvent(new Event("canonical-record-saved"));
      setNotice(
        action === "send"
          ? "Email accepted by Microsoft. Bank acknowledgement is still awaited."
          : "Email approved. Review and send when ready.",
      );
    } catch (cause) {
      setError(cause);
      if (action === "send")
        setNotice(
          "Check delivery status before trying again. An uncertain attempt must be reconciled; it is never automatically resent.",
        );
    } finally {
      await load().catch(setError);
      lock.current = false;
      setBusy(false);
    }
  }
  if (!model) return error ? <ErrorState error={error} /> : <Loading />;
  return (
    <section
      aria-label="NatWest account application"
      style={{ minWidth: 0, overflowWrap: "anywhere" }}
    >
      <h2>Apply to NatWest</h2>
      <p>
        Download the current form, complete and sign it in Adobe, then retain
        the completed copy here. Saif and Leoni each approve their own signature
        before the email is prepared.
      </p>
      {notice && <p role="status">{notice}</p>}
      {Boolean(error) && <ErrorState error={error} />}
      {held && (
        <p>
          The save outcome is not confirmed. Your original request is retained.{" "}
          <button
            disabled={busy}
            onClick={() => {
              const original = request.current;
              if (original) void command(original.path, original.body);
            }}
          >
            Check original save
          </button>
        </p>
      )}
      <button
        className="secondary"
        disabled={busy}
        onClick={() => void load().catch(setError)}
      >
        Refresh application status
      </button>
      <fieldset disabled={busy || held}>
        <legend>Bank application</legend>
        {model.identities.some((i) => !i.userId) && (
          <p>
            Set up the two named signatories before requesting approvals. An
            administrator must link Saif and Leoni to their individual staff
            accounts.
          </p>
        )}
        {model.canPrepare && (
          <button onClick={() => void command("download-form", {})}>
            Download and retain current bank form
          </button>
        )}
        {model.formDownloads.slice(0, 1).map((f) => (
          <p key={f.id}>
            Downloaded {formatTimestampDate(f.downloadedAt)} ·{" "}
            {f.fresh ? "Within two months" : "Expired"} ·{" "}
            {f.printedVersion ?? "New form version: review in Adobe"}
            <br />
            <NatwestFile
              building={building}
              id={f.documentId}
              versionId={f.versionId}
              name="NWB87955.pdf"
            />
            {!f.layoutRecognised &&
              " · The bank form changed; review its account route and declarations before completing it."}
          </p>
        ))}
        {model.canPrepare && (
          <>
            <OnboardingDocumentUpload
              building={building}
              category="bank-application"
              label="completed bank form or bank confirmation"
              returnTo={`/buildings/${building}/setup/financials?section=account-plan`}
              onSaved={() => void load().catch(setError)}
            />
            {!editing ? (
              <button onClick={() => setEditing(true)}>
                Prepare application for signature review
              </button>
            ) : (
              <NatwestPreparation
                model={model}
                command={command}
                onClose={() => setEditing(false)}
              />
            )}
          </>
        )}
        {model.packages.slice(0, 1).map((v) => (
          <PackageReview
            key={v.id}
            building={building}
            model={model}
            version={v}
            command={command}
            onMail={mail}
          />
        ))}
        {model.packages.length > 1 && (
          <details>
            <summary>
              Previous application versions ({model.packages.length - 1})
            </summary>
            {model.packages.slice(1).map((v) => (
              <PackageReview
                key={v.id}
                building={building}
                model={model}
                version={v}
                command={command}
                onMail={mail}
              />
            ))}
          </details>
        )}
        <NatwestBankProgress model={model} command={command} />
      </fieldset>
    </section>
  );
}
export function NatwestFile({
  building,
  id,
  versionId,
  name,
}: {
  building: string;
  id: string;
  versionId: string;
  name: string;
}) {
  const [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  async function download() {
    setBusy(true);
    setError(undefined);
    try {
      const response = await authenticatedFetch(
        `/api/documents/${id}/versions/${versionId}/content?download=1&building=${building}`,
        { cache: "no-store" },
      );
      if (!response.ok)
        throw reviewedApplicationError(
          "The retained document could not be downloaded. Review its access and current status.",
        );
      const url = URL.createObjectURL(await response.blob()),
        a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button
        type="button"
        className="secondary"
        disabled={busy}
        onClick={() => void download()}
      >
        Download {name}
      </button>
      {Boolean(error) && <ErrorState error={error} />}
    </>
  );
}
function PackageReview({
  building,
  model,
  version: v,
  command,
  onMail,
}: {
  building: string;
  model: Model;
  version: Package;
  command: NatwestCommand;
  onMail: (v: Package, action: "review" | "send") => Promise<void>;
}) {
  const [accountId, setAccountId] = useState(model.mailboxes[0]?.id ?? ""),
    [confirm, setConfirm] = useState(false);
  return (
    <article className="queue-card" style={{ display: "block", minWidth: 0 }}>
      <h3>
        Application {v.versionNumber} ·{" "}
        {v.sentAt ? "Submitted" : (v.deliveryStatus ?? "Signature review")}
      </h3>
      <p>{v.snapshot.accounts.map((a) => a.name).join(" · ")}</p>
      <p>
        Applicant: {v.snapshot.applicant.name} ·{" "}
        {v.snapshot.applicant.companyNumber}
      </p>
      <p>
        Prepared {formatTimestampDate(v.createdAt)} ·{" "}
        {v.fresh ? "Form within two months" : "Fresh form needed"}
      </p>
      {v.snapshot.documents.map((d) => (
        <p key={d.version_id}>
          <NatwestFile
            building={building}
            id={d.id}
            versionId={d.version_id}
            name={d.file_name}
          />
        </p>
      ))}
      <details>
        <summary>Exact application reference and preparation review</summary>
        <p>
          {v.id} · {v.packageHash}
        </p>
        <p>{v.snapshot.notes}</p>
        {v.snapshot.revision.reason && (
          <p>Resubmission: {v.snapshot.revision.reason}</p>
        )}
        {v.snapshot.revision.previousExternalSubmission && (
          <p>
            Earlier external submission:{" "}
            {formatDateOnly(
              v.snapshot.revision.previousExternalSubmission.submittedOn,
            )}
            {" · "}
            {v.snapshot.revision.previousExternalSubmission.stage}
            {" · "}
            {v.snapshot.revision.previousExternalSubmission.bankReference}
            <br />
            {v.snapshot.revision.previousExternalSubmission.note}
          </p>
        )}
        <p>
          Adobe saved and reopened; account names, declarations and dates
          reviewed. Digital signature consent below applies only to these
          retained files.
        </p>
      </details>
      {v.approvals.map((a) => (
        <div key={a.requestId}>
          <p>
            {a.signatory}: {a.status}
          </p>
          {a.canDecide && (
            <SignatureDecision command={command} version={v} approval={a} />
          )}
        </div>
      ))}
      {!v.sentAt && v.missing.length > 0 && (
        <ul>
          {v.missing.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
      {!v.draftId && v.readyToSubmit && model.canPrepare && (
        <>
          <label>
            Send from
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              <option value="">Choose authorised mailbox</option>
              {model.mailboxes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · {a.address}
                </option>
              ))}
            </select>
          </label>
          <button
            disabled={!accountId}
            onClick={() =>
              void command("prepare-email", {
                applicationVersionId: v.id,
                accountId,
                confirmExactPackage: true,
              })
            }
          >
            Prepare bank email
          </button>
        </>
      )}
      {v.email && (
        <section aria-label="Bank email preview">
          <h4>Email to NatWest</h4>
          <p>
            From: {v.email.from}
            <br />
            To: {v.email.to.join(", ")}
            {v.email.cc.length > 0 && (
              <>
                <br />
                Cc: {v.email.cc.join(", ")}
              </>
            )}
            {v.email.bcc.length > 0 && (
              <>
                <br />
                Bcc: {v.email.bcc.join(", ")}
              </>
            )}
            <br />
            Subject: {v.email.subject}
          </p>
          <p style={{ whiteSpace: "pre-wrap" }}>{v.email.body}</p>
          <p>
            Attachments:{" "}
            {v.snapshot.documents.map((d) => d.file_name).join(", ")}
          </p>
          {v.readyToSubmit && v.email.canReview && (
            <button onClick={() => void onMail(v, "review")}>
              Approve bank email
            </button>
          )}
          {v.readyToSubmit && v.email.canSend && (
            <>
              <label>
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(e) => setConfirm(e.target.checked)}
                />
                I have reviewed the recipient, message and exact attachments for
                this submission.
              </label>
              <button
                disabled={!confirm}
                onClick={() => void onMail(v, "send")}
              >
                Send application to NatWest
              </button>
            </>
          )}
          {v.threadId && (
            <p>
              <Link to={`/communications/threads/${v.threadId}`}>
                View bank correspondence
              </Link>
            </p>
          )}
        </section>
      )}
      {v.deliveryStatus === "Uncertain" && (
        <p>
          Delivery is uncertain. No automatic retry is allowed.{" "}
          <button
            onClick={() =>
              void command("reconcile", {
                applicationVersionId: v.id,
                confirmReadOnlyCheck: true,
              })
            }
          >
            Check the existing mailbox copy
          </button>
        </p>
      )}
      {v.sentAt && (
        <p>
          Email accepted {formatDateTime(v.sentAt)}. Bank acceptance and account
          opening are tracked separately below.
        </p>
      )}
    </article>
  );
}
function SignatureDecision({
  command,
  version,
  approval,
}: {
  command: NatwestCommand;
  version: Package;
  approval: Package["approvals"][number];
}) {
  const [date, setDate] = useState(""),
    [own, setOwn] = useState(false),
    [names, setNames] = useState(false),
    [decision, setDecision] = useState("Approved"),
    [reason, setReason] = useState("");
  useRecordDraft(Boolean(date || own || names || reason));
  async function save(event: FormEvent) {
    event.preventDefault();
    await command("signature-decision", {
      applicationVersionId: version.id,
      approvalRequestId: approval.requestId,
      expectedApprovalVersion: approval.version,
      decision,
      signingDate: date || null,
      confirmOwnSignature: own,
      confirmNamesAndDates: names,
      reason,
    });
  }
  return (
    <form className="compact-form" onSubmit={save}>
      <label>
        Your decision
        <select value={decision} onChange={(e) => setDecision(e.target.value)}>
          <option>Approved</option>
          <option>Returned</option>
          <option>Rejected</option>
        </select>
      </label>
      {decision === "Approved" && (
        <>
          <label>
            Actual signature date on the form
            <DateOnlyInput required value={date} onChange={setDate} />
          </label>
          <label>
            <input
              required
              type="checkbox"
              checked={own}
              onChange={(e) => setOwn(e.target.checked)}
            />
            I authorise my own signature on these exact completed forms.
          </label>
          <label>
            <input
              required
              type="checkbox"
              checked={names}
              onChange={(e) => setNames(e.target.checked)}
            />
            I reviewed the exact account names, declarations and dates.
          </label>
        </>
      )}
      <label>
        Review note
        <textarea
          required
          minLength={3}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <button>Save my signature decision</button>
    </form>
  );
}
